import {
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ReactNode,
} from "react";

/**
 * ClickDialog UX/UI V1 interaction components.
 * React ownership stays in Platform Web; @clickdialog/ui owns tokens.
 * Source contract: docs/product-ui/CLICKDIALOG_UX_UI_OPERATIONAL_SPEC_V1.md
 */

export type ActionVariant =
  | "primary"
  | "secondary"
  | "tertiary"
  | "ghost"
  | "danger";

export type ActionButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type"> & {
  variant?: ActionVariant;
  loading?: boolean;
  disabledReason?: string;
  children: ReactNode;
  type?: "button" | "submit" | "reset";
};

export function ActionButton({
  variant = "secondary",
  loading = false,
  disabled = false,
  disabledReason,
  children,
  className,
  type = "button",
  ...rest
}: ActionButtonProps) {
  const unavailable = disabled || loading;
  return <span className="cd-action-wrap">
    <button
      {...rest}
      type={type}
      className={["cd-action-button", className].filter(Boolean).join(" ")}
      data-variant={variant}
      data-loading={loading ? "true" : undefined}
      disabled={unavailable}
      aria-busy={loading || undefined}
    >
      {loading && <span className="cd-action-spinner" aria-hidden="true" />}
      {children}
    </button>
    {disabled && disabledReason && <small className="cd-action-disabled-reason">{disabledReason}</small>}
  </span>;
}

export function ActionToolbar({
  children,
  label = "Acciones",
  className,
}: {
  children: ReactNode;
  label?: string;
  className?: string;
}) {
  return <div
    className={["cd-action-toolbar", className].filter(Boolean).join(" ")}
    role="group"
    aria-label={label}
  >{children}</div>;
}

export interface OverflowAction {
  key: string;
  label: string;
  onSelect: () => void;
  disabled?: boolean;
  destructive?: boolean;
}

/**
 * Scoped action menu. WAI-ARIA menu keyboard rules:
 * trigger Enter/Space/ArrowDown opens -> first enabled item,
 * ArrowUp opens -> last; menu ArrowUp/Down/Home/End traverses;
 * Escape returns focus to trigger; Tab dismisses without trapping focus.
 * Danger handlers/confirmation belong to the domain, not this menu.
 */
export function OverflowMenu({
  label,
  actions,
}: {
  label: string;
  actions: readonly OverflowAction[];
}) {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const openFocusRef = useRef<"first" | "last">("first");
  const enabledIndices = actions.flatMap((action, index) => action.disabled ? [] : [index]);

  function moveFocus(index: number) {
    if (enabledIndices.length === 0) return;
    const normalized = (index + enabledIndices.length) % enabledIndices.length;
    itemRefs.current[enabledIndices[normalized]!]?.focus();
  }

  function close(restoreFocus: boolean) {
    setOpen(false);
    if (restoreFocus) window.requestAnimationFrame(() => triggerRef.current?.focus());
  }

  useEffect(() => {
    if (!open) return;
    const onOutside = (event: PointerEvent) => {
      if (
        event.target instanceof Node &&
        !menuRef.current?.contains(event.target) &&
        !triggerRef.current?.contains(event.target)
      ) setOpen(false);
    };
    document.addEventListener("pointerdown", onOutside);
    return () => document.removeEventListener("pointerdown", onOutside);
  }, [open]);

  useEffect(() => {
    if (open) window.requestAnimationFrame(() => moveFocus(openFocusRef.current === "first" ? 0 : enabledIndices.length - 1));
    // Intentionally focus when opening, not on actions changing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (actions.length === 0) return null;

  return <div className="cd-overflow">
    <button
      ref={triggerRef}
      type="button"
      className="cd-overflow-trigger"
      aria-label={label}
      aria-haspopup="menu"
      aria-expanded={open}
      aria-controls={open ? menuId : undefined}
      onClick={() => { openFocusRef.current = "first"; setOpen((value) => !value); }}
      onKeyDown={(event) => {
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
          event.preventDefault();
          openFocusRef.current = event.key === "ArrowDown" ? "first" : "last";
          setOpen(true);
        }
      }}
    >···</button>
    {open && <div
      id={menuId}
      ref={menuRef}
      className="cd-overflow-panel"
      role="menu"
      aria-label={label}
      onKeyDown={(event) => {
        const current = itemRefs.current.indexOf(document.activeElement as HTMLButtonElement);
        const position = enabledIndices.indexOf(current);
        if (event.key === "Escape") { event.preventDefault(); close(true); }
        else if (event.key === "Tab") { setOpen(false); }
        else if (event.key === "ArrowDown") { event.preventDefault(); moveFocus(position + 1); }
        else if (event.key === "ArrowUp") { event.preventDefault(); moveFocus(position - 1); }
        else if (event.key === "Home") { event.preventDefault(); moveFocus(0); }
        else if (event.key === "End") { event.preventDefault(); moveFocus(enabledIndices.length - 1); }
      }}
    >
      {actions.map((action, index) =>
        <button
          key={action.key}
          ref={(element) => { itemRefs.current[index] = element; }}
          type="button"
          role="menuitem"
          tabIndex={-1}
          disabled={action.disabled}
          className="cd-overflow-item"
          data-destructive={action.destructive ? "true" : undefined}
          onClick={() => {
            if (action.disabled) return;
            setOpen(false);
            action.onSelect();
            window.requestAnimationFrame(() => triggerRef.current?.focus());
          }}
        >{action.label}</button>,
      )}
    </div>}
  </div>;
}
